import { createRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Route as AdminLayoutRoute } from "./_layout";
import { Badge, BadgeWithDot } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { TextArea } from "@/components/base/textarea/textarea";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Modal } from "@/components/application/modals/modal-controlled";
import { DataTable, type Column } from "@/components/application/data-table/data-table";
import { PushAssistDetailCard } from "../../components/admin/PushAssistDetailCard";
import {
  useGenerateQr,
  useLogDelivery,
  usePushAssistDetail,
  usePushAssistList,
  useUploadPaperConsent,
  useWithdrawConsent,
} from "../../hooks/usePushAssistQueries";
import type {
  PushAssistUserRow,
  GenerateQrResponse,
} from "../../schemas/pushAssist";
import { ApiError } from "../../lib/apiClient";

/**
 * /admin/push-assist — UC-009 Josefova úhrada (Phase 25).
 *
 * Workflow:
 *   1. Data Table list users s push_assist_mode=TRUE + consent badge.
 *   2. Klik na row → Detail Action Card (PushAssistDetailCard).
 *   3. Upload paper consent → Generate QR (hard-blocked bez consent) → Log delivery.
 */

export const Route = createRoute({
  getParentRoute: () => AdminLayoutRoute,
  path: "/admin/push-assist",
  component: PushAssistPage,
});

function PushAssistPage() {
  const listQuery = usePushAssistList();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const detailQuery = usePushAssistDetail(selectedUserId);
  const uploadMutation = useUploadPaperConsent(selectedUserId ?? "none");
  const withdrawMutation = useWithdrawConsent(selectedUserId ?? "none");
  const generateMutation = useGenerateQr(selectedUserId ?? "none");
  const deliveryMutation = useLogDelivery(selectedUserId ?? "none");

  const [qr, setQr] = useState<GenerateQrResponse | undefined>(undefined);

  // Odvolání souhlasu — in-app modal s důvodem (ne native window.prompt).
  const [withdrawOpen, setWithdrawOpen] = useState<boolean>(false);
  const [withdrawReason, setWithdrawReason] = useState<string>("");

  const columns: Column<PushAssistUserRow>[] = [
    {
      key: "name",
      header: "Uživatel",
      cell: (row) => (
        <div className="flex flex-col">
          <strong className="text-primary">{row.display_name}</strong>
          <span className="text-xs text-tertiary">{row.email}</span>
        </div>
      ),
    },
    {
      key: "consent",
      header: "Souhlas",
      cell: (row) =>
        row.has_valid_paper_consent ? (
          <BadgeWithDot color="success">
            OK
          </BadgeWithDot>
        ) : (
          <BadgeWithDot color="warning">
            Chybí
          </BadgeWithDot>
        ),
    },
    {
      key: "role",
      header: "Role",
      cell: (row) => <Badge color="blue">{row.role}</Badge>,
    },
    {
      key: "actions",
      header: "Akce",
      cell: (row) => (
        <Button
          type="button"
          color="secondary"
          size="sm"
          onPress={() => setSelectedUserId(row.user_id)}
          data-testid={`push-assist-row-${row.user_id}`}
        >
          Otevřít detail
        </Button>
      ),
    },
  ];

  if (listQuery.isLoading) {
    return (
      <section className="max-w-7xl mx-auto space-y-4">
        <h2 className="text-display-xs font-bold text-primary m-0">Push-assist</h2>
        <p className="text-tertiary">Načítání…</p>
      </section>
    );
  }

  if (listQuery.isError) {
    return (
      <section className="max-w-7xl mx-auto space-y-4">
        <h2 className="text-display-xs font-bold text-primary m-0">Push-assist</h2>
        <InlineAlert variant="error" title="Chyba načítání">
          Nelze načíst seznam push-assist uživatelů.
        </InlineAlert>
      </section>
    );
  }

  const users = listQuery.data?.users ?? [];
  const detail = detailQuery.data;
  const withdrawn = !!(
    detail && detail.push_assist_mode && !detail.has_valid_paper_consent &&
    detail.paper_consents.length > 0
  );

  return (
    <section
      aria-labelledby="push-assist-title"
      data-testid="push-assist-page"
      className="max-w-7xl mx-auto space-y-6"
    >
      <header className="flex flex-col gap-1">
        <h2 id="push-assist-title" className="text-display-xs font-bold text-primary m-0">
          Push-assist
        </h2>
        <p className="text-md text-tertiary m-0">
          Uživatelé v push-assist režimu — papírový souhlas + telefonický mód. Před generováním QR
          je nutné nahrát naskenovaný podepsaný souhlas.
        </p>
      </header>

      <DataTable
        caption="Seznam push-assist uživatelů"
        rows={users}
        rowKey={(r) => r.user_id}
        columns={columns}
        emptyMessage="Žádný uživatel není v push-assist režimu."
      />

      {detailQuery.isFetching && <p className="text-tertiary">Načítám detail…</p>}
      {detail && (
        <div className="mt-2">
          <PushAssistDetailCard
            detail={detail}
            // Phase 25 wires the real payment-intent picker UI. Until then, derive
            // a stable placeholder from the freshest paper-consent UUID — present
            // once `has_valid_paper_consent === true`, undefined while the hard
            // block is active (R-026). This keeps the "Generovat QR" button
            // semantically gated on consent (per UC-009 DoD) and the backend
            // fail-fast validates the value server-side (404 if not a real
            // payment_intent). Replace with `usePendingPaymentIntent(userId)` once
            // /api/admin/payment-intents listing exists.
            paymentIntentId={
              detail.deliveries[0]?.delivery_id
              ?? detail.paper_consents[0]?.stored_file_id
            }
            amountKc={1234}
            qr={qr}
            uploadError={uploadMutation.error ? extractMessage(uploadMutation.error) : null}
            generateError={generateMutation.error ? extractMessage(generateMutation.error) : null}
            uploading={uploadMutation.isPending}
            generating={generateMutation.isPending}
            withdrawn={withdrawn}
            onUploadConsent={async (file) => {
              await uploadMutation.mutateAsync(file);
            }}
            onGenerateQr={async (paymentIntentId) => {
              const response = await generateMutation.mutateAsync(paymentIntentId);
              setQr(response);
            }}
            onLogDelivery={(input) => {
              deliveryMutation.mutate({
                delivery_channel: input.delivery_channel,
                note: input.note,
                payment_intent_id: input.payment_intent_id,
                qr_code_id: input.qr_code_id,
              });
            }}
          />
          <Button
            type="button"
            color="tertiary"
            size="sm"
            onPress={() => {
              setWithdrawReason("");
              setWithdrawOpen(true);
            }}
            data-testid="push-assist-withdraw-button"
          >
            Odvolat souhlas
          </Button>
        </div>
      )}

      <Modal
        open={withdrawOpen}
        title="Odvolat souhlas"
        onClose={() => {
          if (withdrawMutation.isPending) return;
          setWithdrawOpen(false);
        }}
        footer={
          <>
            <Button
              color="secondary"
              onPress={() => setWithdrawOpen(false)}
              isDisabled={withdrawMutation.isPending}
            >
              Zrušit
            </Button>
            <Button
              color="primary-destructive"
              isDisabled={withdrawReason.trim().length === 0 || withdrawMutation.isPending}
              onPress={() => {
                withdrawMutation.mutate(withdrawReason.trim());
                setQr(undefined);
                setWithdrawOpen(false);
              }}
              data-testid="push-assist-withdraw-confirm"
            >
              {withdrawMutation.isPending ? "Odvolávám…" : "Odvolat souhlas"}
            </Button>
          </>
        }
      >
        <InlineAlert variant="warning">
          Odvolání zneplatní stávající souhlas a zablokuje generování platebních QR
          kódů pro tohoto uživatele, dokud nebude nahrán nový podepsaný souhlas.
        </InlineAlert>
        <div className="mt-4">
          <TextArea
            label="Důvod odvolání"
            isRequired
            value={withdrawReason}
            onChange={setWithdrawReason}
            rows={3}
            isDisabled={withdrawMutation.isPending}
          />
        </div>
      </Modal>
    </section>
  );
}

function extractMessage(err: unknown): string {
  if (err instanceof ApiError) return err.body?.message ?? err.message;
  if (err instanceof Error) return err.message;
  return "Neznámá chyba";
}
